import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { getPatientMedications, getBpPeriods } from '../data/mockPatientMedications';
import PrescriptionModal from './PrescriptionModal';

function formatEventDate(dateStr) {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function buildEventLabel(row) {
  const name = row.drug_name.charAt(0).toUpperCase() + row.drug_name.slice(1);
  switch (row.event_type) {
    case 'initiation':      return `Started ${name} ${row.dose}`;
    case 'dose-increase':   return `Increased ${name} to ${row.dose}`;
    case 'dose-decrease':   return `Decreased ${name} to ${row.dose}`;
    case 'renewal':         return `Renewed ${name} ${row.dose}`;
    case 'discontinuation': return `Discontinued ${name}`;
    default:                return `${name} ${row.dose}`;
  }
}

export default function MedicationReviewModal({ patient, onClose }) {
  const [medications, setMedications] = useState(null); // null = loading
  const [bpPeriods, setBpPeriods]     = useState([]);
  const [showPrescribe, setShowPrescribe] = useState(false);

  // Replace with the real Platform API query when the entity is ready (see mockPatientMedications.js)
  useEffect(() => {
    getPatientMedications(patient.userid).then(rows => {
      setMedications(rows);
      setBpPeriods(getBpPeriods(patient.userid));
    });
  }, [patient.userid]);

  function handlePrescriptionSaved(newRow) {
    setMedications(prev => [...prev, newRow]);
  }

  // Disable body scroll while modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // Close on Escape key
  useEffect(() => {
    function handleKey(e) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return createPortal((
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="modal-header">
          <div>
            <h2 className="header3" style={{ margin: 0, textTransform: 'capitalize' }}>
              {patient.firstname} {patient.lastname}
            </h2>
            <p className="detail1 text-secondary" style={{ margin: '4px 0 0' }}>
              Current BP: {patient.lastSystolic} mmHg &nbsp;·&nbsp; Target gap: +{patient.targetGap} mmHg
            </p>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>

        {/* Timeline */}
        <div className="timeline-container">
          {medications === null ? (
            <div className="loading-center" style={{ padding: '24px 0' }}>
              <div className="loading-spinner" />
              <span className="loading-center-text">Loading medications…</span>
            </div>
          ) : (
            medications.map((row, i) => (
              <React.Fragment key={row.medication_id}>
                {/* Medication event */}
                <div className="timeline-event">
                  <div className="timeline-dot" />
                  <div>
                    <span className="body2" style={{ fontWeight: 500 }}>{buildEventLabel(row)}</span>
                    <span className="detail1 text-secondary" style={{ display: 'block', marginTop: 2 }}>
                      {formatEventDate(row.prescribed_at)}
                    </span>
                  </div>
                </div>

                {/* Period summary card (between this event and the next) */}
                {i < bpPeriods.length && (
                  <>
                    <div className="timeline-line" />
                    <div className="timeline-period-card">
                      <div className="period-summary">
                        <div>
                          <span className="eyebrow text-secondary">Avg systolic</span>
                          <span className="body2" style={{ display: 'block', fontWeight: 600, marginTop: 2 }}>
                            {bpPeriods[i].avgSystolic} mmHg
                          </span>
                        </div>
                        <div>
                          <span className="eyebrow text-secondary">Avg diastolic</span>
                          <span className="body2" style={{ display: 'block', fontWeight: 600, marginTop: 2 }}>
                            {bpPeriods[i].avgDiastolic} mmHg
                          </span>
                        </div>
                        <div>
                          <span className="eyebrow text-secondary">Max reading</span>
                          <span className="body2" style={{ display: 'block', fontWeight: 600, marginTop: 2, color: 'var(--wi-status-bad)' }}>
                            {bpPeriods[i].maxSystolic} mmHg
                          </span>
                          <span className="detail1 text-secondary" style={{ display: 'block' }}>
                            {formatEventDate(bpPeriods[i].maxDate)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="timeline-line" />
                  </>
                )}
              </React.Fragment>
            ))
          )}
        </div>

        {/* Prescribe action */}
        <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid var(--wi-divider)', display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
          <button className="btn-secondary" onClick={onClose}>Close</button>
          <button className="btn-primary" onClick={() => setShowPrescribe(true)}>
            Prescribe medication
          </button>
        </div>
      </div>

      {/* Prescription form (opens on top) */}
      {showPrescribe && (
        <PrescriptionModal
          patient={patient}
          medications={medications ?? []}
          onSaved={handlePrescriptionSaved}
          onClose={() => setShowPrescribe(false)}
        />
      )}
    </div>
  ), document.body);
}
